<template>
  <div :class="$style.wrapper">
    <div v-if="areConditionsEmpty" :class="$style.emptyState">No conditions set</div>
    <div v-if="!areConditionsEmpty" :class="$style.row">
      <div v-if="conditionsRef" :class="$style.row">
        <SegmentsDropDown
          :model-value="conditionsRef.segments || []"
          :segments="segments"
          :default-selected="defaultSegment"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('segments', eventPayload)"
        />

        <SkipFrequencyDropdown
          :model-value="conditionsRef.skipFrequency || 0"
          :segments="segments"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('skipFrequency', eventPayload)"
        />
        <DailyLimitDropDown
          :model-value="conditionsRef.dailyLimit || 0"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('dailyLimit', eventPayload)"
        />
        <CooldownConditionsDropdown
          v-if="conditionsRef.coolDown"
          :model-value="conditionsRef.coolDown"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('coolDown', eventPayload)"
        />
        <ActiveDaysDropdown
          :model-value="conditionsRef.activeDays || []"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('activeDays', eventPayload)"
        />
        <ActiveTimeDropdown
          v-if="conditionsRef.activeTime"
          :model-value="conditionsRef.activeTime"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('activeTime', eventPayload)"
        />
        <OfferTrigger
          :triggers="triggers"
          :game-id="gameId"
          :is-editable="isOfferEditable"
          @update:selected-triggers="handleTriggersUpdate"
        />
        <OfferTag
          :tags="tags"
          :game-id="gameId"
          :version-id="versionId"
          :is-editable="isOfferEditable"
          @update:tags="handleTagsUpdate"
        />
        <DiscountInPercentDropDown
          v-if="isGameMobControlOrWeAreInDev"
          :model-value="conditionsRef.discountInPercent || 0"
          :disabled="!isOfferEditable"
          @update="(eventPayload) => handleOfferConditionUpdate('discountInPercent', eventPayload)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ActiveDaysDropdown from "@/components/ActiveDaysDropdown";
import ActiveTimeDropdown from "@/components/ActiveTimeDropdown";
import CooldownConditionsDropdown from "@/components/CooldownConditionsDropdown";
import DailyLimitDropDown from "@/components/DailyLimitDropdown";
import DiscountInPercentDropDown from "@/components/DiscountInPercentDropdown";
import SegmentsDropDown from "@/components/SegmentsDropDown";
import SkipFrequencyDropdown from "@/components/SkipFrequencyDropdown";
import { useSegments } from "@/modules/Offers/useSegments";
import { getNonNullProperties, GRAPH_QL_ADDITIONAL_PROPERTIES } from "@/utils/getNonNullProperties";
import { computed, ref, Ref, watch } from "vue";
import { useRoute } from "vue-router";
import OfferTag from "./OfferTag.vue";
import OfferTrigger from "./OfferTrigger.vue";

const viteEnvironment = import.meta.env.VITE_ENVIRONMENT;

const isDevelopment = ["dev", "local"].includes(viteEnvironment);
const route = useRoute();
const gameId = ref<string>(route.params.gameId as string);
const versionId = ref<string>(route.params.versionId as string);

interface ActiveTime {
  from: string;
  to: string;
}

interface CoolDownInput {
  days: number;
  hours: number;
}

interface Conditions {
  activeDays: string[];
  activeTime: ActiveTime | null;
  coolDown?: CoolDownInput;
  dailyLimit: number | null;
  rewardMultiplier: number | null;
  skipFrequency?: number | null;
  segments?: string[] | null;
  discountInPercent: number | null;
}
const conditionsRef: Ref<Partial<Conditions>> = ref({});

const props = defineProps({
  conditions: {
    type: Object,
    default: () => ({}),
  },
  triggers: {
    type: Array,
    default: () => [],
  },
  tags: {
    type: Array,
    default: () => [],
  },
  isOfferEditable: {
    type: Boolean,
    default: true,
  },
});

watch(
  () => props.conditions,
  (newVal) => {
    conditionsRef.value = {
      ...newVal,
    };
  },
  { deep: true, immediate: true }
);

const emits = defineEmits(["update", "update:triggers", "update:tags"]);

const nonNullConditions = computed(() => getNonNullProperties(conditionsRef.value, GRAPH_QL_ADDITIONAL_PROPERTIES));
const areConditionsEmpty = computed<boolean>(() => nonNullConditions.value.length === 0);
const { segments } = useSegments(gameId.value);

const defaultSegment = computed(() => segments.value?.find((segment) => segment.name === "All users")?.id);

const isGameMobControlOrWeAreInDev = computed(
  () => gameId.value === "877e6e50-3ee3-4174-bc61-62f31ddb587b" || isDevelopment
);
const handleOfferConditionUpdate = (key: string, value: unknown) => {
  emits("update", {
    ...props.conditions,
    [key]: value,
  });
};

const handleTriggersUpdate = (payload: unknown) => {
  emits("update:triggers", payload);
};

const handleTagsUpdate = (payload: unknown) => {
  emits("update:tags", payload);
};
</script>

<style module>
.wrapper {
  border-radius: 16px;
  background-color: var(--vd-color-neutral-light-10);
  padding: 1rem;
  position: relative;
  width: 100%;
  min-height: 100px;
  text-align: left;
}

.emptyState {
  font-size: 14px;
  font-weight: 600;
  color: var(--vd-color-text-light-2);
  position: absolute;
  top: 2rem;
  left: 2rem;
}

.row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
}
</style>
